/**
 * IONLOOP Assistant — controlled synonym and alias map.
 *
 * Browser-safe static alias definitions plus a builder that derives
 * additional synonyms from the knowledge index (titles, headings, keywords).
 */

const STATIC_ALIASES = {
    'ionloop id':           ['id', 'identity', 'account identity', 'single sign on', 'ionloop identity'],
    'auto pay':             ['autopay', 'automated payments', 'automate payments', 'auto-pay', 'recurring payments', 'schedule payments', 'payment automation'],
    'trade panel':          ['trading dashboard', 'trading panel', 'trade panel', 'trading workspace'],
    'sheet editor':         ['spreadsheet', 'sheets', 'sheet', 'excel', 'table editor'],
    'diagram builder':      ['diagram', 'diagrams', 'flowchart', 'diagramming'],
    'calendar builder':     ['calendar', 'calender', 'scheduling', 'calendar tool'],
    'signal explorer':      ['signal', 'signals', 'public market', 'signal finder'],
    'cycle constructor':    ['cycle', 'cycles', 'cycle builder', 'cycle tool'],
    'trading history':      ['market tools', 'trade history', 'history'],
    'account levels':       ['pricing', 'plans', 'tiers', 'plan', 'account plans', 'subscription'],
    'help center':          ['support', 'contact', 'faq', 'help', 'customer service'],
    'recover account':      ['recover', 'password reset', 'forgot password', 'lost password', 'account recovery', 'reset password'],
    'business operations':  ['business tools', 'operations', 'business workspace', 'business', 'erp', 'enterprise resource planning'],
    'wallet':               ['payment', 'payments', 'deposit', 'withdraw', 'transfer', 'crypto wallet', 'wallet tool'],
    'security':             ['2fa', 'two-factor', 'two factor', 'authenticate', 'authentication', 'verification', 'verify', 'device', 'encryption', 'secure'],
    'pos':                  ['point of sale', 'omnichannel payments', 'payment terminal'],
    'spot trading':         ['spot', 'spot trade', 'spot market'],
    'margin trading':       ['margin', 'margin trade'],
    'futures trading':      ['futures', 'futures trade'],
    'notification system':  ['notifications', 'alerts', 'notification', 'alert'],
    'notes':                ['notes++', 'note', 'notepad'],
    'time tracker':         ['time tracking', 'track time', 'timesheet'],
    'appointment builder':  ['appointments', 'booking', 'appointment', 'bookings'],
    'file sharing':         ['p2p', 'share files', 'sharing', 'file share'],
    'file encryption':      ['encrypt files', 'file security', 'encryption tool'],
    'getting started':      ['onboarding', 'get started', 'create account', 'open account', 'new account', 'start'],
    'trading tools':        ['trading', 'trade', 'trading workspace', 'trading dashboard'],
    'planning tools':       ['planning', 'planner', 'planning tool'],
    'account security':     ['security tools', 'secure account', 'account protection'],
    'convert':              ['converter', 'conversion', 'crypto converter', 'convert crypto'],
    'planner constructor':  ['planner', 'plan constructor', 'strategy planner'],
    'campaign builder':     ['campaign', 'campaigns'],
    'edge constructor':     ['edge', 'edge builder'],
    'public market':        ['marketplace', 'signal explorer'],
    'news source':          ['news', 'trending news', 'news feed'],
    'ionloop care':         ['care', 'support plan', 'care plan'],
    'download':             ['desktop app', 'mobile app', 'app download', 'client'],
    'pricing':              ['price', 'cost', 'costs', 'subscription', 'subscribe'],
    'account':              ['profile', 'settings', 'account settings', 'my account']
};

/**
 * Build a reverse map: alias term → canonical concept.
 */
function buildReverseMap() {
    const map = {};
    Object.keys(STATIC_ALIASES).forEach(function (canonical) {
        var terms = [canonical].concat(STATIC_ALIASES[canonical]);
        terms.forEach(function (term) {
            var key = term.toLowerCase().replace(/\s+/g, ' ').trim();
            if (!map[key]) {
                map[key] = [];
            }
            if (map[key].indexOf(canonical) === -1) {
                map[key].push(canonical);
            }
        });
    });
    return map;
}

var REVERSE_MAP = null;

function getReverseMap() {
    if (!REVERSE_MAP) {
        REVERSE_MAP = buildReverseMap();
    }
    return REVERSE_MAP;
}

/**
 * Given a query term, find canonical concepts and their alias expansions.
 * Returns an array of { term, weight } where weight is 1.0 for exact
 * canonical, 0.6 for synonym.
 */
function expandTerm(term) {
    var results = [];
    var key = term.toLowerCase().replace(/\s+/g, ' ').trim();
    var canonicals = getReverseMap()[key];

    if (canonicals) {
        canonicals.forEach(function (canonical) {
            // The canonical phrase itself.
            results.push({ term: canonical, weight: 0.6 });
            // Its aliases (excluding the matched term).
            (STATIC_ALIASES[canonical] || []).forEach(function (alias) {
                var aliasKey = alias.toLowerCase().replace(/\s+/g, ' ').trim();
                if (aliasKey !== key) {
                    results.push({ term: aliasKey, weight: 0.6 });
                }
            });
        });
    }

    // Also include the original term itself at full weight if it's not a
    // very short common word.
    if (term.length >= 2) {
        results.push({ term: term, weight: 1.0 });
    }

    // Deduplicate.
    var seen = {};
    var deduped = [];
    results.forEach(function (r) {
        if (!seen[r.term]) {
            seen[r.term] = true;
            deduped.push(r);
        }
    });
    return deduped;
}

/**
 * Check if a text contains a multi-word phrase (case-insensitive, normalized).
 */
function textContainsPhrase(text, phrase) {
    if (!text || !phrase) {
        return false;
    }
    var normText = ' ' + text.toLowerCase().replace(/[^a-z0-9\s]+/g, ' ') + ' ';
    var normPhrase = ' ' + phrase.toLowerCase().replace(/[^a-z0-9\s]+/g, ' ') + ' ';
    return normText.indexOf(normPhrase) !== -1;
}

module.exports = {
    STATIC_ALIASES: STATIC_ALIASES,
    getReverseMap: getReverseMap,
    expandTerm: expandTerm,
    textContainsPhrase: textContainsPhrase,
    buildReverseMap: buildReverseMap
};